import React, { useState } from 'react';
import { Form, FormInstance, Input } from 'antd';
import { CREATE_WORKER } from '@scheduling/components';
import {
  Drawer,
  PrimaryButton,
  SecondaryButton,
  ButtonGroup,
  Icon,
  Banner,
  Gutter,
  Text,
  Link,
} from '@common/components';
import {
  WorksMembershipByEmailQuery,
  MembershipByEmail,
} from 'works/graphql/works-membership-by-email';
import { useLazyQuery, useMutation } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Store } from 'antd/lib/form/interface';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';
import { SEND_APP_INVITATION_MUTATION } from '@scheduling/graphql';
import { WorkerCreateForm } from './_worker-create-form';
import { buildDialogUrl } from '@common/services';

export function WorkerCreateDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const { canShowAdminMembers } = useCurrentMembership();
  const [validEmail, setValidEmail] = useState(false);
  const [managerMembership, setManagerMembership] =
    useState<MembershipByEmail>();
  const [workerMembershipFound, setWorkerMembershipFound] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [sendAppInvitation] = useMutation(SEND_APP_INVITATION_MUTATION, {
    onCompleted(data) {
      const { message } = data?.sendAppInvitation || {};
      if (message) {
        createMessage('success', message);
      }
    },
  });

  const [createWorker] = useMutation(CREATE_WORKER, {
    onCompleted(response) {
      if (response?.createWorker?.errors?.length) {
        addErrorsToForm(form, response.createWorker.errors);
        return;
      }

      const workerId = response?.createWorker?.worker?.id;
      const sendAppInvite = form.getFieldValue('sendAppInvite');

      if (workerId && sendAppInvite) {
        sendAppInvitation({ variables: { workerId } });
      }

      setFormSubmitting(false);
      onClose();
    },
    refetchQueries: ['PaginatedWorkersQuery'],
    onError: () => {
      setFormSubmitting(false);
      createMessage(
        'error',
        'Failed to create Worker Details. Please try again or contact your hiring partner.'
      );
    },
  });

  const [findMembership, { loading }] = useLazyQuery(
    WorksMembershipByEmailQuery,
    {
      onCompleted(data) {
        if (data.worksMembershipByEmail?.role === 'worker') {
          setValidEmail(false);
          setWorkerMembershipFound(true);
        } else {
          setValidEmail(true);
          setWorkerMembershipFound(false);
          setManagerMembership(data.worksMembershipByEmail);
        }
      },
    }
  );

  const searchUserByEmail = async (email: string) => {
    try {
      await form.validateFields(['email']);
      findMembership({
        variables: { email: email, includeWorkers: true, workerFirst: true },
      });
    } catch {
      // If the email is invalid, don't proceed with the mutation
      return;
    }
  };

  const onFormFinish = (values: Store) => {
    setFormSubmitting(true);

    const homeUnit = values.allowedUnits.find(
      (unit: { isHomeUnit: boolean; unit: string }) => unit.isHomeUnit
    )?.unit;

    const allowLists = values.allowedUnits.flatMap(
      (allowedUnit: { roles: string[]; unit: string }) =>
        allowedUnit.roles.map((role) => ({
          organizationUnitId: allowedUnit.unit,
          roleId: role,
        }))
    );

    const input = {
      workerInput: {
        active: true,
        isInternal: true,
        homeUnitId: homeUnit,
        roleId: values.roleId,
        employeeId: values.employeeId,
        workforceGroupId: values.workforceGroupId,
      },
      worksUserInput: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
      },
      allowLists: allowLists,
    };

    createWorker({ variables: input });
  };

  const managerViewDrawerUrl = buildDialogUrl('user-view-drawer', {
    props: { userId: managerMembership?.id || '' },
    pathName: '/works/admin/users',
    overlap: false,
  });

  const emailHelperText = workerMembershipFound
    ? 'Staff with this email already exists.'
    : managerMembership
      ? 'We recognized this email and pre-filled available details.'
      : undefined;

  return (
    <Drawer
      size="lg"
      open={open}
      onClose={onClose}
      title="Add Staff"
      footer={
        <Footer
          onClose={onClose}
          form={form}
          formSubmitting={formSubmitting}
          submitDisabled={!validEmail}
        />
      }
    >
      {!workerMembershipFound && managerMembership && (
        <Gutter direction="bottom" size="lg">
          <Banner
            type="info"
            content={
              <>
                This staff profile is linked to an Admin user account. Some
                details can’t be edited here — edit email, name, or mobile
                number in the{' '}
                <Text bold underline>
                  {canShowAdminMembers ? (
                    <Link to={managerViewDrawerUrl}>Admin user</Link>
                  ) : (
                    'Admin user'
                  )}
                </Text>
                .
              </>
            }
          />
        </Gutter>
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ sendAppInvite: true }}
        onFinish={onFormFinish}
      >
        <Form.Item
          name="email"
          label="Email"
          required
          validateStatus={workerMembershipFound ? 'error' : undefined}
          help={emailHelperText}
          rules={[
            {
              required: true,
              message: 'Please enter an email address',
            },
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input
            size="large"
            placeholder="Type email to start"
            disabled={validEmail}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !validEmail) {
                e.preventDefault();
                searchUserByEmail(form.getFieldValue('email'));
              }
            }}
            suffix={
              validEmail && (
                <Icon icon="checkCircle" kind="solid" color="grass" />
              )
            }
          />
        </Form.Item>

        {!validEmail && (
          <SecondaryButton
            onClick={() => searchUserByEmail(form.getFieldValue('email'))}
            size="large"
            loading={loading}
            disabled={loading}
          >
            Validate Email
          </SecondaryButton>
        )}

        {validEmail && (
          <WorkerCreateForm form={form} membershipFound={managerMembership} />
        )}
      </Form>
    </Drawer>
  );
}

const Footer = ({
  onClose,
  form,
  formSubmitting,
  submitDisabled,
}: {
  onClose: () => void;
  form: FormInstance;
  formSubmitting: boolean;
  submitDisabled: boolean;
}) => (
  <ButtonGroup>
    <SecondaryButton size="large" onClick={onClose}>
      Cancel
    </SecondaryButton>
    <PrimaryButton
      size="large"
      onClick={form.submit}
      loading={formSubmitting}
      disabled={submitDisabled}
    >
      Add Staff
    </PrimaryButton>
  </ButtonGroup>
);
